<template>
    <div class="tw-min-h-screen tw-bg-primary-100">
        <section class="tw-py-10 tw-text-center">
            <span class="tw-text-sm tw-font-medium tw-uppercase tw-tracking-widest tw-text-secondary">A nossa arte</span>
            <h1 class="tw-font-serif tw-mt-4 tw-text-4xl tw-text-secondary md:tw-text-6xl">Portefólio</h1>
            <p class="tw-mx-auto tw-mt-2 tw-max-w-2xl tw-px-4 tw-text-gray-600">
                Uma viagem visual pelas nossas criações mais doces. Cada trabalho é um projeto único, desenhado com amor em Pitões das Júnias.
            </p>
            <p class="tw-mx-auto tw-mt-4 tw-max-w-xl tw-px-4 tw-text-sm tw-font-medium tw-text-[#6E3C4B]">
                {{ tabHint }}
            </p>
        </section>

        <div
            id="portfolio-tabs"
            class="portfolio-tabs tw-sticky tw-z-30 tw-border-b tw-border-secondary/10 tw-bg-primary-100">
            <div
                class="tw-mx-auto tw-flex tw-max-w-5xl tw-justify-start tw-gap-1 tw-overflow-x-auto tw-px-4 md:tw-justify-center"
                role="tablist">
                <button
                    v-for="tab in portfolioTabs"
                    :key="tab.id"
                    type="button"
                    role="tab"
                    class="tw-shrink-0 tw-border-b-2 tw-px-4 tw-py-4 tw-font-serif tw-text-lg md:tw-px-6"
                    :class="activeTab === tab.id ? 'tw-border-secondary tw-text-secondary' : 'tw-border-transparent tw-text-gray-500'"
                    :aria-selected="activeTab === tab.id"
                    @click="showTab(tab.id)">
                    {{ tab.label }}
                </button>
            </div>
        </div>

        <section class="tw-mx-auto tw-max-w-[1400px] tw-px-4 tw-py-10">
            <Transition
                name="folio"
                mode="out-in">
                <div :key="activeTab">
                    <p
                        v-if="visiblePhotos.length === 0"
                        class="tw-py-20 tw-text-center tw-text-secondary">
                        Ainda não há fotos neste separador.
                    </p>
                    <div
                        v-else
                        class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 sm:tw-gap-6 xl:tw-grid-cols-4">
                        <article
                            v-for="item in visiblePhotos"
                            :key="item.id"
                            class="tw-relative tw-aspect-[4/5] tw-overflow-hidden tw-bg-primary-200"
                            :class="item.kind === 'fatia' ? '' : 'tw-cursor-pointer'"
                            @click="selectPhoto(item)">
                            <v-img
                                :src="item.fotoUrl"
                                cover
                                class="tw-h-full tw-w-full tw-transition-transform tw-duration-700"
                                :class="selectedId === item.id ? 'tw-scale-105' : ''" />
                            <div
                                v-if="selectedId === item.id && orderLabel(item)"
                                class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-secondary/45 tw-p-3">
                                <button
                                    type="button"
                                    class="tw-rounded-full tw-bg-[#FBF6E8] tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-[#6E3C4B]"
                                    @click.stop="orderThis(item)">
                                    {{ orderLabel(item) }}
                                </button>
                            </div>
                        </article>
                    </div>
                </div>
            </Transition>
        </section>
    </div>
</template>

<script lang="ts" setup>
    import { useRouter } from 'vue-router';
    import { createClient } from '@supabase/supabase-js';

    const router = useRouter();

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

    type CakeCategory = 'bento' | 'florais' | 'vintage' | 'personalizados' | 'pasta' | 'modelagens';
    type PhotoKind = 'bolo' | 'miniatura' | 'bolacha' | 'fatia';
    type PortfolioTab = 'bolos' | 'miniaturas' | 'bolachas' | 'fatias' | 'modelagens';

    type PortfolioCake = {
        id: string;
        fotoUrl: string;
        name: string;
        category?: CakeCategory;
        kind?: PhotoKind;
    };

    const portfolioTabs: { id: PortfolioTab; label: string }[] = [
        { id: 'bolos', label: 'Bolos' },
        { id: 'miniaturas', label: 'Miniaturas' },
        { id: 'bolachas', label: 'Bolachas' },
        { id: 'fatias', label: 'Fatias' },
        { id: 'modelagens', label: 'Modelagens' },
    ];

    const orderLabel = (item: PortfolioCake) => {
        if (item.kind === 'fatia') return '';
        if (item.kind === 'miniatura') return 'Encomendar miniaturas';
        if (item.kind === 'bolacha') return 'Encomendar bolachas';
        return 'Encomendar este bolo';
    };

    const orderThis = (item: PortfolioCake) => {
        if (!orderLabel(item)) return;
        sessionStorage.setItem('atelier-inspiration', JSON.stringify({ name: item.name, fotoUrl: item.fotoUrl, kind: item.kind || 'bolo' }));
        router.push({ name: 'Order', query: { inspiracao: '1' } });
    };

    const getCakes = async () => {
        if (!supabase) return [];
        const { data, error } = await supabase.from('cake').select('*');
        if (error) {
            console.error('Error fetching cakes:', error);
            return [];
        }
        return data;
    };

    const cakes = ref<any[]>([]);

    const fetchCakes = async () => {
        let c = [];
        try {
            c = await getCakes();
        } catch (error) {
            console.error('Error fetching cakes:', error);
        } finally {
            cakes.value = c.filter((cake: any) => cake.seeInWebSIte === true);
        }
    };

    fetchCakes();

    const getImageUrl = (path: string) => {
        const cleanPath = path.replace('@/', '');
        return new URL(`/src/${cleanPath}`, import.meta.url).href;
    };

    const photo = (file: string, name: string, kind: PhotoKind): PortfolioCake => ({
        id: file,
        fotoUrl: getImageUrl(`assets/images/nePhotos/${file}`),
        name,
        kind,
    });

    const atelierPhotos: PortfolioCake[] = [
        photo('IMG_9861.JPG', 'Cookies', 'bolacha'),
        photo('IMG_9863.JPG', "Witches don't age", 'bolo'),
        photo('IMG_9864.JPG', 'Tarte de nata', 'bolo'),
        photo('IMG_9865.JPG', 'Os três porquinhos', 'bolo'),
        photo('IMG_9866.JPG', 'Bolachas safari', 'bolacha'),
        photo('IMG_9868.JPG', 'Ratatouille', 'bolo'),
        photo('IMG_9869.jpg', 'Um conto de fadas', 'bolo'),
        photo('IMG_9870.jpg', 'O leão aniversariante', 'bolo'),
        photo('IMG_9871.jpg', 'Unicórnio', 'bolo'),
        photo('IMG_9872.jpg', 'Limão e flores', 'bolo'),
        photo('IMG_9873.jpg', 'Panda', 'bolo'),
        photo('IMG_9874.jpg', 'Octógono', 'bolo'),
        photo('IMG_9875.jpg', 'Cheesecake de frutos vermelhos', 'bolo'),
        photo('IMG_9876.jpg', 'Flores', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.11.59 (2).jpeg', 'Ratatouille', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00.jpeg', 'Bolachas Ratatouille', 'bolacha'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00 (3).jpeg', 'Chocolate e pistácio', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00 (4).jpeg', 'Fatia de chocolate', 'fatia'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00 (5).jpeg', 'Fatia de cenoura', 'fatia'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00 (6).jpeg', 'Croissants', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.00 (7).jpeg', 'Croissants', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01.jpeg', 'Croissants', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (1).jpeg', 'Folhados', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (2).jpeg', 'Pastéis de nata', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (3).jpeg', 'Caracóis de chocolate', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (4).jpeg', 'Um conto de fadas', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (5).jpeg', 'Cookies e brownies', 'bolacha'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (6).jpeg', 'Tiramisu', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (7).jpeg', 'Fatia de tiramisu', 'fatia'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (8).jpeg', 'Bolachas de doce', 'bolacha'),
        photo('WhatsApp Image 2026-10-23 at 10.12.01 (9).jpeg', 'Brownie', 'miniatura'),
        photo('WhatsApp Image 2026-10-23 at 10.12.02.jpeg', 'Laços amarelos', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.12.02 (1).jpeg', 'Laços amarelos', 'bolo'),
        photo('WhatsApp Image 2026-10-23 at 10.17.39 (2).jpeg', 'Luana', 'bolo'),
    ];

    const modelagem = (file: string, name: string): PortfolioCake => ({
        id: file,
        fotoUrl: getImageUrl(`assets/images/nePhotos/${file}`),
        name,
        category: 'modelagens',
    });

    const newModelagens: PortfolioCake[] = [
        modelagem('rato2.JPG', 'Remy'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (1).jpeg', 'Panda'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (4).jpeg', 'Leão aniversariante'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (5).jpeg', 'Lobo mau'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (6).jpeg', 'Quinta'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (7).jpeg', 'Homem-Aranha'),
        modelagem('WhatsApp Image 2026-10-23 at 10.17.39 (8).jpeg', 'Bluey'),
    ];

    const newCakesArray = [
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (2).jpeg'),
            name: 'Bolo Tradição Portuguesa',
            description:
                'Uma homenagem às raízes portuguesas para o 1º aniversário! Decoração lateral a imitar o clássico azulejo português, flores vibrantes e uma linda bonequinha com traje típico minhoto.',
            price: '115',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07.jpeg'),
            name: 'Bolo Quinta da Sofia',
            description:
                'Uma verdadeira festa na quinta! Bolo super colorido para celebrar os 4 anos, cheio de animais fofinhos modelados à mão (porquinho na lama, ovelha, burro, cãozinho e patos) e uma menina camponesa.',
            price: '130',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.05 (2).jpeg'),
            name: 'Bolo Coelhinha da Alma',
            description:
                'Bolo de dois andares em tons pastel suaves (rosa e verde). Decorado com flores silvestres e borboletas delicadas, coroado com uma adorável coelhinha modelada à mão em pasta de açúcar.',
            price: '125',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.05 (1).jpeg'),
            name: 'Bolo Safari no Jipe',
            description:
                'Bolo de um andar com o tema Safari, ideal para o 1º aniversário. Destaca-se pelas manchas de leopardo e fantásticas modelagens de animais: um leão no topo e um macaquinho, girafa e elefante num divertido jipe.',
            price: '95',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.04 (2).jpeg'),
            name: 'Bolo Bosque Encantado',
            description: 'Bolo de um andar que nos transporta para um conto de fadas. Decorado com cogumelos mágicos em tons pastel, flores silvestres de açúcar e lindas borboletas peroladas.',
            price: '75',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0528.PNG'),
            name: 'Bolo Flores Clássicas',
            description:
                'Bolo de um andar pautado pela elegância e delicadeza. Cobertura branca texturizada e belíssimas flores de creme espatuladas em tons de azul e branco. Pode ser personalizado com mensagem no centro.',
            price: '75',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_8286.jpeg'),
            name: 'Bento Cake Ursinho e Balões',
            description:
                'Mini bolo muito meigo e delicado, ideal para celebrar o 3º mesversário. Apresenta um ursinho com textura realista em relevo a segurar balões azuis, complementado com pequenas pérolas de açúcar brilhantes.',
            price: '45',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.04 (1).jpeg'),
            name: 'Bento Cake Leãozinho',
            description: 'Delicado bolo festivo, com um adorável leãozinho desenhado à mão com creme colorido. Um design alegre e muito fofinho para celebrar os 4 meses do bebé.',
            price: '40',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (3).jpeg'),
            name: 'Bolo Bosque da Beatriz',
            description:
                'Bolo mágico e encantador para o primeiro aniversário, com o tema de bosque. Destaca-se o adorável cervo modelado, rodeado de cogumelos, flores delicadas e borboletas esvoaçantes.',
            price: '90',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0477.PNG'),
            name: 'Bolo Panda Vermelho',
            description:
                'Bolo super divertido e expressivo em forma de panda vermelho (estilo Turning Red). Cobertura vibrante laranja e detalhes muito fofos modelados à mão em pasta de açúcar. Inclui topo "Parabéns" e velas.',
            price: '85',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0486.PNG'),
            name: 'Bolo Kuromi',
            description:
                'Bolo adorável e cheio de personalidade com o tema Kuromi. Cuidadosamente decorado com rosetas de creme em tons de rosa e lilás, coraçõezinhos, laços de cetim e a personagem perfeitamente modelada no topo.',
            price: '95',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_1096.jpeg'),
            name: 'Bolo Semi-Naked Frutos Vermelhos',
            description:
                'Bolo de dois andares estilo semi-naked (rústico), generosamente decorado com uma cascata de frutos vermelhos frescos (morangos, framboesas, mirtilos e groselhas) e delicada gypsophila.',
            price: '140',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0514.PNG'),
            name: 'Bolo Horta Criativa',
            description:
                'Bolo temático muito original e divertido a imitar uma horta. Decorado com "terra" comestível, legumes modelados em pasta de açúcar (cenouras, couves e tomates) e flores coloridas na base.',
            price: '85',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0505.PNG'),
            name: 'Bolo Aventura Frozen',
            description:
                'O bolo de sonho para os fãs da magia do gelo! Inclui imagem da Elsa, Anna e Olaf, complementado com topos de um castelo azul, flocos de neve e um bonito trabalho de bico em creme cor-de-rosa.',
            price: '80',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0503.PNG'),
            name: 'Bento Cake Ursinho Pooh',
            description:
                'Prático e adorável Bento Cake entregue na sua caixinha ecológica. Destaca-se por um desenho feito à mão em creme do clássico Ursinho Pooh a segurar um balão vermelho personalizado.',
            price: '40',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_0216.jpeg'),
            name: 'Bolo de Frutas Frescas',
            description:
                'Fresco, vibrante e delicioso! Bolo com cobertura suave de creme e um rebordo clássico, coroado com uma seleção generosa de frutos vermelhos e fatias de laranja cristalizadas.',
            price: '65',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (14).jpeg'),
            name: 'Bolo de Casamento Clássico',
            description: 'Elegante bolo de dois andares com cobertura de creme espatulada e decoração de rosas brancas frescas. Perfeito para celebrar o amor.',
            price: '150',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (13).jpeg'),
            name: 'Bolo de Batizado Personalizado',
            description: 'Adorável bolo de dois andares para batizado, com topos de bonecos feitos à mão e uma faixa dourada personalizada com o nome. Massa leve e saborosa.',
            price: '125',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (12).jpeg'),
            name: 'Bolo de Aniversário Peppa Pig Inês',
            description: 'Bolo vibrante de dois andares para o aniversário da Inês. Com todas as personagens principais e topos temáticos de arco-íris e nuvens. Inclui vela de número 2.',
            price: '135',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (11).jpeg'),
            name: 'Bolo de Borboletas Sofia',
            description: 'Bolo de um andar com gradiente pastel e topos de borboletas douradas e azuis. Inclui um aro dourado personalizado com o nome Sofia e o número 9.',
            price: '95',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (10).jpeg'),
            name: 'Bolo de Frutos Silvestres e Flor',
            description: 'Bolo rústico e sofisticado com bagas frescas (framboesas e mirtilos) e uma grande flor comestível no centro. Ideal para festas de verão.',
            price: '75',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (9).jpeg'),
            name: 'Bolo de 50 Anos Elegante',
            description: 'Bolo de um andar para celebrar meio século. Cobertura de creme com textura e uma flor comestível. Inclui o número 50 em dourado, entregue na caixa de transporte.',
            price: '85',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (8).jpeg'),
            name: 'Bolo Espatulado Flores Coloridas',
            description: 'Bolo moderno e alegre com textura de espátula e decoração de flores coloridas em tons vibrantes de rosa, vermelho e verde. Perfeito para qualquer celebração.',
            price: '65',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (7).jpeg'),
            name: 'Bolo de Batizado Gêmeas com Cruz',
            description: 'Um bolo de dois andares único para o batizado de gêmeas. Com topos de bonecas gêmeas e uma cruz dourada, decorado com pétalas em tons de rosa. Massa premium.',
            price: '140',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (5).jpeg'),
            name: 'Bolo Fazendeiro',
            description:
                'Bolo rústico com tema de quinta. Inclui drip de caramelo, musgo comestível e detalhes modelados à mão em pasta de açúcar, como um fazendeiro, uma vaquinha e fardos de palha.',
            price: '85',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (4).jpeg'),
            name: 'Bolo Caça e Glamour',
            description:
                'Um bolo incrivelmente criativo e personalizado que junta dois mundos: o da caça (com javali, caçadeira e balas) e o da beleza (com pincéis, maquilhagens de marcas de luxo e saco de compras).',
            price: '95',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (8).jpeg'),
            name: 'Bolo Happy Birthday Macarons',
            description: 'Design minimalista e requintado com cobertura espatulada suave. Decorado com macarons com toques dourados, mirtilos frescos, folhagem verde e um elegante topo dourado.',
            price: '75',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06.jpeg'),
            name: 'Bolo Safari do Anxo',
            description:
                'Bolo de formato retangular com o alegre tema de safari. Perfeito para o primeiro aninho, incluindo leão, elefante, girafa, macaquinho e diversas folhas de selva em pasta de açúcar.',
            price: '105',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.04.jpeg'),
            name: 'Bolo Floresta do Ursinho Pooh',
            description:
                'Delicioso bolo decorado com o tema clássico do Pooh. Massa fofa e cobertura de creme com detalhes em pasta de açúcar e biscoito. Inclui vela personalizada e topo de madeira.',
            price: '110',
        },
    ];

    const firts9cakes = [
        {
            fotoUrl: getImageUrl('assets/images/622bcc75-7cbe-4631-a75f-163ca2c987e0.jpeg'),
            name: 'Cowboy Cake',
            description: 'O equilíbrio perfeito entre a massa de esponja e a frescura dos frutos silvestres da época. Decorado à medida com detalhes elegantes e personalizados à medida do pedido.',
            price: '40',
        },
        {
            fotoUrl: getImageUrl('assets/images/31765d04-4147-444c-aa7e-0b719a4ac6c6.jpeg'),
            name: 'Mermaid Cake',
            description: 'O equilíbrio perfeito entre a massa de esponja e a frescura dos frutos silvestres da época. Decorado à medida com detalhes elegantes e personalizados à medida do pedido.',
            price: '50',
        },
        {
            fotoUrl: getImageUrl('assets/images/287690a8-d234-4d3f-ab17-26b4c17096f9.jpeg'),
            name: 'Bear Cake',
            description: 'O equilíbrio perfeito entre a massa de esponja e a frescura dos frutos silvestres da época. Decorado à medida com detalhes elegantes e personalizados à medida do pedido.',
            price: '100',
        },
    ];

    const sugarFigures = [
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.07 (1).jpeg'),
            name: 'Menino e Coelhinho',
            description: 'Um menino sonhador numa cesta rústica texturizada acompanhado pelo seu amigo coelhinho.',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-28 at 10.44.31.jpeg'),
            name: 'Ursinho nas Nuvens',
            description: 'Adorável ursinho modelado à mão, com laço delicado, sentado sobre um céu de nuvens fofinhas.',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (7).jpeg'),
            name: 'Alegria na Quinta',
            description: 'Cenário completo de quinta com uma menina camponesa, um cão fiel, um burro, patinhos e um porquinho a rebolar na lama.',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (6).jpeg'),
            name: 'Meninas Gémeas',
            description: 'Duas doces bonequinhas gémeas de mãos dadas, com vestidos rosa e bandoletes de flores.',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (5).jpeg'),
            name: 'Casalinho',
            description: 'Topo de bolo minimalista e muito querido representando um menino e uma menina de mãos dadas.',
        },
        {
            fotoUrl: getImageUrl('assets/images/WhatsApp Image 2026-07-27 at 21.30.06 (1).jpeg'),
            name: 'Menina Minhota',
            description: 'Bonequinha vestida a rigor com o traje tradicional minhoto, incluindo o lenço na cabeça e avental bordado.',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_9823.jpeg'),
            name: 'Os Animais da Selva',
            description: 'Conjunto dos amiguinhos da selva, o leão, a girafa, o macaquinho e o elefante.',
        },
        {
            fotoUrl: getImageUrl('assets/images/IMG_9853.jpeg'),
            name: 'Safari dos Amigos',
            description: 'Topo de bolo com o tema safari, incluindo a girafa, o macaquinho e o elefante.',
        },
    ];

    const hiddenFolderFiles = new Set([
        'd20b9c50-1876-4e9a-a721-7eafc31fe6a8.jpeg',
        'dde7e742-c62b-4bba-a6a7-7d55edd4ed00.jpeg',
        '8ffcb9ab-3a55-4561-ae11-57972da63fab.jpeg',
        '5f1650fa-0671-4865-a162-370201230df1.jpeg',
        '1d924cc6-053c-4cd6-a7e7-b3b3b0a7c297.jpeg',
        'd54af549-b3ac-4e23-b71d-53fef0ed07de.jpeg',
        'fd6fdf5d-4db0-4a07-94fb-0c34af040d45.jpeg',
        'aff9a703-af3e-4fb9-b1f4-1f8a6707f195.jpeg',
        'a6d84ca7-b532-4f45-bf0e-e7f61af8b8be.jpeg',
        'adb931c7-d93b-4390-879a-1571d9a0d36d.jpeg',
        'ee4ebf6c-3536-431d-a562-39d5a806eb42.jpeg',
        '5f231c65-7eab-4547-8fb3-6deb0893ca1e.jpeg',
        '66805997-a605-42c8-af54-c5fe3fee7649.jpeg',
        '31aac050-9086-4e6f-9e8f-cf9395dee90e.jpeg',
    ]);

    const inferCategory = (name: string, path = ''): CakeCategory | undefined => {
        const hay = `${path} ${name}`.toLowerCase();
        if (hay.includes('bento')) return 'bento';
        if (/\b(floral|flores|florais|flor)\b/.test(hay)) return 'florais';
        if (hay.includes('vintage')) return 'vintage';
        if (hay.includes('sugar_paste') || hay.includes('pasta')) return 'pasta';
        if (hay.includes('personalized') || hay.includes('personaliz') || hay.includes('topper')) return 'personalizados';
        return undefined;
    };

    const fileKey = (url: string) => {
        const segment = url.split(/[?#]/)[0].split('/').pop() || url;
        try {
            return decodeURIComponent(segment);
        } catch {
            return segment;
        }
    };

    const upsert = (list: PortfolioCake[], item: PortfolioCake) => {
        const key = fileKey(item.fotoUrl);
        const existing = list.find((entry) => fileKey(entry.fotoUrl) === key);
        if (!existing) {
            list.push(item);
            return;
        }
        if (item.name && !existing.name) {
            existing.name = item.name;
            existing.fotoUrl = item.fotoUrl;
            existing.id = item.id;
        }
        if (item.category === 'modelagens' || !existing.category) existing.category = item.category;
    };

    const staticCakes: PortfolioCake[] = [];

    for (const cake of [...newCakesArray, ...firts9cakes]) {
        upsert(staticCakes, {
            id: cake.fotoUrl,
            fotoUrl: cake.fotoUrl,
            name: cake.name || '',
            category: inferCategory(cake.name || '', cake.fotoUrl || ''),
        });
    }

    for (const figure of sugarFigures) {
        upsert(staticCakes, {
            id: `modelagem-${figure.name}`,
            fotoUrl: figure.fotoUrl,
            name: figure.name,
            category: 'modelagens',
        });
    }

    const allItems = computed(() => {
        const items = staticCakes.map((item) => ({ ...item }));
        const keys = new Set(items.map((item) => fileKey(item.fotoUrl)));
        for (const cake of cakes.value) {
            const fotoUrl = cake?.fotoUrl as string | undefined;
            if (!fotoUrl || hiddenFolderFiles.has(fileKey(fotoUrl))) continue;
            const key = fileKey(fotoUrl);
            const existing = items.find((item) => fileKey(item.fotoUrl) === key);
            if (existing) {
                if (cake.name && !existing.name) existing.name = cake.name as string;
                continue;
            }
            keys.add(key);
            const name = (cake.name as string) || '';
            items.push({
                id: `remote-${cake.id ?? fotoUrl}`,
                fotoUrl,
                name,
                category: inferCategory(name, fotoUrl),
            });
        }
        return items;
    });

    const cakePhotos = computed(() => [...atelierPhotos.filter((item) => item.kind === 'bolo'), ...allItems.value.filter((item) => item.category !== 'modelagens')]);
    const modelagens = computed(() => [...newModelagens, ...allItems.value.filter((item) => item.category === 'modelagens')]);

    const activeTab = ref<PortfolioTab>('bolos');
    const selectedId = ref('');
    const photosByKind = (kind: PhotoKind) => atelierPhotos.filter((item) => item.kind === kind);
    const visiblePhotos = computed(() => {
        if (activeTab.value === 'modelagens') return modelagens.value;
        if (activeTab.value === 'miniaturas') return photosByKind('miniatura');
        if (activeTab.value === 'bolachas') return photosByKind('bolacha');
        if (activeTab.value === 'fatias') return photosByKind('fatia');
        return cakePhotos.value;
    });
    const tabHint = computed(() => {
        if (activeTab.value === 'miniaturas') return 'Carrega numa fotografia para encomendar miniaturas.';
        if (activeTab.value === 'bolachas') return 'Carrega numa fotografia para encomendar bolachas.';
        if (activeTab.value === 'fatias') return 'Fatias para veres o interior. Estas fotografias não abrem um pedido.';
        return 'Carrega numa fotografia para encomendar esse bolo.';
    });

    const selectPhoto = (item: PortfolioCake) => {
        if (item.kind === 'fatia') return;
        selectedId.value = selectedId.value === item.id ? '' : item.id;
    };

    const showTab = (tab: PortfolioTab) => {
        selectedId.value = '';
        activeTab.value = tab;
        const tabs = document.getElementById('portfolio-tabs');
        if (!tabs) return;
        const chrome = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ticker-h')) + parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-h'));
        const top = tabs.getBoundingClientRect().top + window.scrollY - (Number.isFinite(chrome) ? chrome : 0);
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    };
</script>

<style scoped>
    .portfolio-tabs {
        top: calc(var(--ticker-h, 36px) + var(--nav-h, 80px));
    }

    .folio-enter-active,
    .folio-leave-active {
        transition: opacity 0.35s ease, transform 0.35s ease;
    }

    .folio-enter-from,
    .folio-leave-to {
        opacity: 0;
        transform: scale(0.98);
    }
</style>
